import { generateShortNameAndBrand } from 'actions'
import HotelAutocomplete from 'components/ReusableComponents/HotelAutocomplete'
import AddUserIcon from 'components/ReusableComponents/Icons/AddUser'
import GuestIcon from 'components/ReusableComponents/Icons/Guest'
import ManageUserDialog from 'features/ManageUserDialog'
import NewHotelDialog from 'features/NewHotelDialog'
import useCopyText from 'hooks/useCopyText'
import { memo, useEffect, useState } from 'react'
import { useToasts } from 'react-toast-notifications'

import subsidiariesApi from '../../../services/api/subsidiaries'
import CopyIcon from '../Icons/CopyIcon'
import styles from './EditableHotelList.module.scss'
import StepsStatus from './StepsStatus'

const EditableHotelList = ({
  initialHotels = [],
  onChange,
  isEditable = true,
  canAddUsers = true,
  merchantId,
  showUsers = false,
  onNewUserAdded,
  showStepInfo = false,
  hotelSetupStatuses,
  onHotelAdded,
  contractDocuments
}) => {
  const copy = useCopyText()
  const [hotels, setHotels] = useState([])

  const [isContactDialogVisible, setIsContactDialogVisible] = useState(false)
  const [activeHotelToEditContact, setActiveHotelToEditContact] = useState(null)

  const [showAddHotelDialog, setShowAddHotelDialog] = useState(false)
  const [newHotelData, setNewHotelData] = useState()

  const [hotelsAutoCompleteKey, setHotelsAutoCompleteKey] = useState(0)

  const { addToast } = useToasts()

  useEffect(() => {
    if (!onChange) return
    onChange(hotels)
  }, [hotels, onChange])

  useEffect(() => {
    if (initialHotels.length > 0) {
      setHotels(
        initialHotels.map(hotel => {
          const { shortName, brand } = generateShortNameAndBrand(
            hotel.hotelName.includes(hotel.city)
              ? hotel.hotelName
              : `${hotel.hotelName} ${hotel.city}`
          )

          return {
            ...hotel,
            shortName,
            brand
          }
        })
      )
    }
  }, [initialHotels])

  function addHotel(hotel) {
    if (hotels.find(h => h.placesId === hotel.placesId)) {
      addToast(`${hotel.hotelName} is already added`, {
        appearance: 'error',
        autoDismiss: true
      })
      setHotelsAutoCompleteKey(key => key + 1)

      return
    }

    const {
      hotelName,
      city,
      stateCode,
      countryCode,
      postalCode,
      streetAddress,
      placesId
    } = hotel

    const fullHotelName = !hotelName.includes(city) ? `${hotelName} ${city}` : hotelName

    setNewHotelData({
      hotelName: fullHotelName,
      city,
      stateCode,
      countryCode,
      postalCode,
      streetAddress,
      placesId
    })
    setShowAddHotelDialog(true)
  }

  function handleClickEditContact(hotel) {
    setActiveHotelToEditContact(hotel)
    setIsContactDialogVisible(true)
  }

  function closeContactDialog() {
    setIsContactDialogVisible(false)
    setActiveHotelToEditContact(null)
    if (onNewUserAdded) {
      onNewUserAdded()
    }
  }

  function handleHotelCreated(res) {
    setHotels(hotels => [...hotels, res])
    if (onHotelAdded) {
      onHotelAdded()
    }
  }

  function copyUserLoginLink(subsidiaryId, userId) {
    subsidiariesApi
      .getHotelSetupLoginLink({
        subsidiaryId,
        userId
      })
      .then(res => {
        copy(res.createdLink)
      })
      .catch(err => {
        console.warn(err)
      })
  }

  return (
    <>
      {showAddHotelDialog && (
        <NewHotelDialog
          onAdded={handleHotelCreated}
          onClose={() => setShowAddHotelDialog(false)}
          city={newHotelData.city}
          stateCode={newHotelData.stateCode}
          countryCode={newHotelData.countryCode}
          postalCode={newHotelData.postalCode}
          streetAddress={newHotelData.streetAddress}
          placesId={newHotelData.placesId}
          hotelName={newHotelData.hotelName}
          merchantId={merchantId}
          contractDocuments={contractDocuments}
        />
      )}
      {isContactDialogVisible && (
        <ManageUserDialog
          handleAddedUser={closeContactDialog}
          handleClose={closeContactDialog}
          subsidiaryId={activeHotelToEditContact.id}
          isAddingDuringSetup
        />
      )}
      {hotels && hotels.length > 0 && (
        <div className={styles.hotelList}>
          {hotels.map((hotel, index) => (
            <div key={hotel.id}>
              <div className={styles.hotel}>
                {showStepInfo && (
                  <div className={styles.hotelTooltip}>
                    <StepsStatus
                      step={
                        hotelSetupStatuses?.find(s => s.subsidiaryId === hotel.id)
                          ?.activeStepKind
                      }
                      isSetupCompleted={
                        hotelSetupStatuses?.find(s => s.subsidiaryId === hotel.id)
                          ?.isCompleted
                      }
                    />
                  </div>
                )}
                <div className={styles.hotelIndex}>#{index + 1}</div>
                <div className={styles.hotelNameGroup}>
                  {hotel.brand && <p className={styles.hotelBrand}>{hotel.brand}</p>}
                  <p className={styles.hotelShortName}>{hotel.shortName}</p>
                </div>
                <div className={styles.deleteColumn}>
                  {canAddUsers && (
                    <>
                      {hotel.contact ? (
                        <button
                          onClick={() => handleClickEditContact(hotel)}
                          className={styles.editContactButton}
                        >
                          <GuestIcon />
                          {hotel.contact.fullName}
                        </button>
                      ) : (
                        <button
                          className={styles.addContactButton}
                          onClick={() => handleClickEditContact(hotel)}
                        >
                          <AddUserIcon />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              {showUsers && (
                <div>
                  {hotel.users &&
                    hotel.users.length > 0 &&
                    hotel.users.map(user => (
                      <div key={user.userId} className={styles.contactTile}>
                        <svg
                          width="15"
                          height="16"
                          viewBox="0 0 15 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7.5 0.500336C3.36 0.500336 0 3.86034 0 8.00034C0 12.1403 3.36 15.5003 7.5 15.5003C11.64 15.5003 15 12.1403 15 8.00034C15 3.86034 11.64 0.500336 7.5 0.500336ZM7.5 2.75034C8.745 2.75034 9.75 3.75534 9.75 5.00034C9.75 6.24534 8.745 7.25034 7.5 7.25034C6.255 7.25034 5.25 6.24534 5.25 5.00034C5.25 3.75534 6.255 2.75034 7.5 2.75034ZM7.5 13.4003C5.625 13.4003 3.9675 12.4403 3 10.9853C3.0225 9.49284 6 8.67534 7.5 8.67534C8.9925 8.67534 11.9775 9.49284 12 10.9853C11.0325 12.4403 9.375 13.4003 7.5 13.4003Z"
                            fill="#A8ADB2"
                          />
                        </svg>

                        <div className={styles.userInfoWrapper}>
                          <p className={styles.nameText}>{user.name}</p>
                          <p className={styles.emailText}>{user.email}</p>
                        </div>

                        <button
                          className={styles.copyButton}
                          onClick={() => copyUserLoginLink(hotel.id, user.userId)}
                        >
                          <CopyIcon className={styles.copyIcon} />
                          <p className={styles.copyText}>Copy Link</p>
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {isEditable && (
        <div className={styles.hotelAutoCompleteRow}>
          <div className={styles.label}>
            Add {hotels.length > 0 ? 'another' : 'an'} hotel
          </div>
          <HotelAutocomplete onChange={addHotel} key={hotelsAutoCompleteKey} />
        </div>
      )}
    </>
  )
}

function areEqual(prevProps, nextProps) {
  if (prevProps.isEditable !== nextProps.isEditable) {
    return false
  }

  if (!(prevProps?.initialHotels?.length || nextProps?.initialHotels?.length)) {
    return false
  }

  if (prevProps.contractDocuments?.length !== nextProps.contractDocuments?.length) {
    return false
  }

  if (
    prevProps.initialHotels.reduce(
      (acc, hotel) => acc + (hotel.users || []).length,
      0
    ) !==
    nextProps.initialHotels.reduce((acc, hotel) => acc + (hotel.users || []).length, 0)
  ) {
    return false
  }

  const longerArray =
    nextProps.initialHotels.length > prevProps.initialHotels.length
      ? nextProps.initialHotels
      : prevProps.initialHotels
  const shorterArray =
    nextProps.initialHotels.length > prevProps.initialHotels.length
      ? prevProps.initialHotels
      : nextProps.initialHotels

  if (prevProps.hotelSetupStatuses?.length !== nextProps.hotelSetupStatuses?.length) {
    return false
  }

  return longerArray.every(hotel => shorterArray.find(h => h.placesId === hotel.placesId))
}

export default memo(EditableHotelList, areEqual)
